import { useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { computeQuoteValidity, fmtDate } from "./format";

// =========================================================================
// HERO
// =========================================================================
// Long proposal titles shrink so the hero stays balanced, the largest
// size only applies to short references.
const titleSizeClass = (t) => {
  const len = (t || "").length;
  if (len > 90) return "text-xl md:text-2xl lg:text-3xl";
  if (len > 50) return "text-2xl md:text-3xl lg:text-4xl";
  return "text-3xl md:text-4xl lg:text-5xl";
};

// "Expiring in X days" urgency badge: only renders inside the 14-day
// run-up to expiry. Outside that window the costs grid already has
// the calmer "Valid until …" chip (and the dimmed banner once
// expired), this is the loud reminder we surface in the hero so a
// client returning two weeks before the deadline can't miss it.
function HeroExpiryBadge({ proposalDate }) {
  const validity = computeQuoteValidity(proposalDate);
  if (!validity || validity.expired) return null;
  const { daysRemaining, expiresToday } = validity;
  if (!expiresToday && daysRemaining >= 14) return null;
  // Inside 7 days → red, otherwise amber. Today is its own copy:
  // "Expires today" reads stronger than "Expiring in 0 days".
  const critical = daysRemaining <= 7;
  const cls = critical
    ? "border-[#FCA5A5]/60 bg-[#7F1D1D]/45 text-[#FECACA]"
    : "border-[#FCD34D]/60 bg-[#78350F]/40 text-[#FCD34D]";
  const dot = critical ? "bg-[#FCA5A5]" : "bg-[#FCD34D]";
  const label = expiresToday
    ? "Expires today"
    : `Expiring in ${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`;
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] tracking-wider font-medium mb-5 ${cls}`}
      data-testid="hero-expiry-badge"
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${dot} animate-pulse`}
      />
      <Clock className="w-3 h-3" aria-hidden />
      {label}
    </span>
  );
}

export function HeroBand({ d, action = null }) {
  return (
    <header className="hero-band bg-[#0B1220] text-stone-100 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 30%, #3EA8DC 0%, transparent 45%), radial-gradient(circle at 85% 70%, #1B4F8A 0%, transparent 50%)",
        }}
      />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10 lg:py-14">
        {/* Logo + title (left) · proof-of-experience carousel (right) */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 sm:gap-10 lg:gap-14">
          <div className="flex-1 min-w-0">
          <img
            src="/brand/medicys-logo.png"
            alt="Medicys · Healthcare Fieldwork Experts"
            className="w-full max-w-[420px] h-auto mb-6 -ml-5"
          />
          <div className="flex items-baseline gap-4 mb-4 flex-wrap">
            <p className="text-sm tracking-[0.25em] uppercase text-[#3EA8DC] font-medium">
              Proposal #{d.proposal_number ?? "-"}
            </p>
            {d.indication && (
              <p className="text-sm tracking-[0.2em] uppercase text-stone-300 font-medium">
                · {d.indication}
              </p>
            )}
          </div>
          <h1
            className={`font-serif font-light leading-[1.1] ${titleSizeClass(
              d.project_reference
            )} text-white mb-5 max-w-3xl`}
            data-testid="proposal-title"
          >
            {d.project_reference || "Proposal"}
          </h1>
          <HeroExpiryBadge proposalDate={d.proposal_date} />
          <div className="flex flex-col gap-y-2 text-base text-stone-100">
            {d.client_company && (
              <span data-testid="proposal-client">
                <span className="text-stone-400">Prepared for · </span>
                {d.client_company}
                {d.recipient ? ` · ${d.recipient}` : ""}
              </span>
            )}
            {d.prepared_by && (
              <span>
                <span className="text-stone-400">By · </span>
                Medicys Ltd, {d.prepared_by}
              </span>
            )}
            {fmtDate(d.proposal_date) && (
              <span>
                <span className="text-stone-400">Date · </span>
                {fmtDate(d.proposal_date)}
              </span>
            )}
          </div>
          {action && <div className="mt-7 no-print" data-testid="hero-action">{action}</div>}

          </div>

          {/* Proof of experience, matching case studies */}
          <HeroCaseStudies cs={d.case_studies} />
        </div>
      </div>
    </header>
  );
}

// "We've been here before", case-study carousel to the right of the
// proposal title. Each card replicates the medicysltd.co.uk design:
// white card with a notched top-right corner, illustration, blue
// category line, Therapy Area / Topic / Respondent rows (+N pill),
// bold objective and an expandable "Show more" with the full detail.
// Hidden on print (the Assumptions narrative keeps the plain link).
const csInfoRow = (label, children) => (
  <p className="text-[11px] leading-relaxed">
    <span className="text-stone-500">{label}: </span>
    <span className="font-medium text-stone-800">{children}</span>
  </p>
);

// Industry memberships & accreditations, an auto-scrolling marquee
// strip (logos supplied by Medicys, light marks on #000B24 navy).
// Rendered in the page footer under every tab. Pauses on hover.
const ACCREDITATIONS = [
  { src: "/accreditations/bhbia.png", alt: "BHBIA member" },
  { src: "/accreditations/ephmra.png", alt: "EphMRA member" },
  { src: "/accreditations/esomar.png", alt: "ESOMAR corporate member" },
  { src: "/accreditations/enepp.png", alt: "ENEPP member" },
  { src: "/accreditations/cyber-essentials.png", alt: "Cyber Essentials Certified Plus" },
  { src: "/accreditations/crown-commercial.png", alt: "Crown Commercial Service supplier" },
];

export function Accreditations({ className = "" }) {
  // Two identical sequences -> a seamless -50% translate loop.
  const row = [...ACCREDITATIONS, ...ACCREDITATIONS];
  return (
    <div
      className={`rounded-md overflow-hidden no-print ${className}`}
      style={{ backgroundColor: "#000B24" }}
      data-testid="hero-accreditations"
    >
      <style>{`
        @keyframes acc-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .acc-track:hover { animation-play-state: paused; }
      `}</style>
      <p className="text-[9px] tracking-[0.25em] uppercase text-stone-400 text-center pt-3">
        Memberships &amp; accreditations
      </p>
      <div className="relative py-4">
        <div
          className="acc-track flex items-center w-max"
          style={{ animation: "acc-marquee 30s linear infinite" }}
        >
          {row.map((a, i) => (
            <img
              key={i}
              src={a.src}
              alt={a.alt}
              title={a.alt}
              loading="lazy"
              className="h-11 w-auto object-contain shrink-0 mr-16"
              data-testid={
                i < ACCREDITATIONS.length
                  ? `accreditation-${a.src.split("/").pop().replace(".png", "")}`
                  : undefined
              }
            />
          ))}
        </div>
        {/* edge fades into the panel navy */}
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 w-16 pointer-events-none"
          style={{ background: "linear-gradient(to right, #000B24, transparent)" }}
        />
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 w-16 pointer-events-none"
          style={{ background: "linear-gradient(to left, #000B24, transparent)" }}
        />
      </div>
    </div>
  );
}

function HeroCaseStudies({ cs }) {
  const [idx, setIdx] = useState(0);
  if (!cs?.studies?.length) return null;
  const count = cs.studies.length;
  const total = cs.total_matches || count;
  const s = cs.studies[idx % count];
  const go = (delta) => {
    setIdx((idx + delta + count) % count);
  };
  // No per-study pages exist on the site, but its ?s= search filter is
  // applied on load, deep-link to the our-work page searched for this
  // study's own objective text.
  let host = "";
  let href = cs.url;
  try {
    const u = new URL(cs.url);
    host = u.hostname.replace(/^www\./, "");
    u.searchParams.set("s", s.objective || cs.topic || "");
    href = u.toString();
  } catch {
    /* keep cs.url */
  }
  const cap = (t) =>
    String(t || "").replace(/\b\w/g, (c) => c.toUpperCase());
  const respondents = (s.respondent_type || "")
    .split(/[,/]/)
    .map((x) => x.trim())
    .filter(Boolean);
  return (
    <div
      className="w-full lg:w-[420px] shrink-0 lg:pt-12 no-print"
      data-testid="hero-case-studies"
    >
      <style>{`
        @keyframes hero-cs-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {/* header */}
      <div className="mb-3">
        <p className="text-xs tracking-[0.25em] uppercase text-[#3EA8DC] font-medium">
          We&apos;ve been here before
          {total ? (
            <span data-testid="hero-cs-count">
              {" "}
              ({total} time{total === 1 ? "" : "s"}
              {cs.match_level !== "topic" && cs.match_label
                ? ` in ${cs.match_label}`
                : ""}
              )
            </span>
          ) : null}
        </p>
      </div>

      {/* website-replica card */}
      <div key={idx} style={{ animation: "hero-cs-in 0.35s ease-out both" }}>
        <div
          className="relative bg-white rounded-md p-5 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.55)]"
          style={{
            clipPath:
              "polygon(0 0, calc(100% - 48px) 0, 100% 48px, 100% 100%, 0 100%)",
          }}
          data-testid={`case-study-item-${idx}`}
        >
          {s.image && (
            <img
              src={s.image}
              alt=""
              loading="lazy"
              onError={(e) => (e.currentTarget.style.display = "none")}
              className="float-right w-44 h-auto mt-1 mr-6 ml-3 mb-1"
            />
          )}
          <p className="text-[11px] font-semibold text-[#2E9BD6] mb-2">
            {cap(s.category) || "Healthcare Market Research"}
          </p>
          <div className="space-y-0.5">
            {s.therapy_area && csInfoRow("Therapy Area", s.therapy_area)}
            {s.topic && csInfoRow("Topic", s.topic)}
            {respondents.length > 0 &&
              csInfoRow("Respondent Type", respondents.join(", "))}
          </div>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="case-study-objective"
            className="block mt-3 text-[15px] font-semibold leading-snug text-stone-900 hover:text-[#1B4F8A] transition-colors"
          >
            {s.objective}
          </a>
          <div
            className="mt-3 space-y-1.5 text-xs text-stone-600"
            data-testid="case-study-detail"
          >
            {s.description &&
              s.description.trim() !== (s.objective || "").trim() && (
                <p>{s.description}</p>
              )}
            {s.methodology && csInfoRow("Methodology", s.methodology)}
            {s.sample_size && csInfoRow("Sample size", `n=${s.sample_size}`)}
            {s.services && csInfoRow("Services delivered", s.services)}
            {s.countries?.length > 0 &&
              csInfoRow(
                "Markets",
                <span className="inline-flex items-center gap-x-2 gap-y-0.5 flex-wrap align-middle">
                  {s.countries.map((c, i) => (
                    <span key={c} className="inline-flex items-center gap-1">
                      {s.flags?.[i] && (
                        <img
                          src={s.flags[i]}
                          alt=""
                          loading="lazy"
                          onError={(e) =>
                            (e.currentTarget.style.display = "none")
                          }
                          className="w-4 h-3 object-cover rounded-[1px]"
                        />
                      )}
                      {c}
                    </span>
                  ))}
                </span>
              )}
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 pt-1 text-xs font-medium text-[#2E9BD6] hover:text-[#1B4F8A] transition-colors"
            >
              View on {host || "our website"}
              <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* carousel controls */}
      <div className="flex items-center justify-between mt-3 gap-4">
        {count <= 8 ? (
          <div className="flex items-center gap-1.5">
            {cs.studies.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Case study ${i + 1}`}
                data-testid={`hero-cs-dot-${i}`}
                className={`h-1.5 rounded-full transition-[width,background-color] ${
                  i === idx
                    ? "w-5 bg-[#3EA8DC]"
                    : "w-1.5 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        ) : (
          <div
            className="h-1.5 flex-1 max-w-[180px] rounded-full bg-white/15 overflow-hidden"
            data-testid="hero-cs-progress"
          >
            <div
              className="h-full bg-[#3EA8DC] rounded-full transition-[width] duration-300"
              style={{ width: `${((idx + 1) / count) * 100}%` }}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous case study"
            data-testid="hero-cs-prev"
            className="w-7 h-7 rounded-full border border-white/15 text-stone-300 hover:border-[#3EA8DC] hover:text-white flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[10px] text-stone-300 tracking-wider tabular-nums">
            {idx + 1}/{count}
          </span>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next case study"
            data-testid="hero-cs-next"
            className="w-7 h-7 rounded-full border border-white/15 text-stone-300 hover:border-[#3EA8DC] hover:text-white flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
